/**
 * TanStack Query hooks for QA Content Checks.
 *
 * Fetches qa_runs and qa_check_results from Supabase REST API.
 */

import { useQuery } from '@tanstack/react-query';
import { qaContentCheckService } from '../services/qaContentCheckService';
import type { QARun, QACheckResult, QAContentCheckFilters } from '../types/qaContentChecks';

export const qaContentCheckKeys = {
  all: ['qa-content-checks'] as const,
  runs: (tenantId: string, filters: QAContentCheckFilters) =>
    [...qaContentCheckKeys.all, 'runs', tenantId, filters] as const,
  checkResults: (runId: string) =>
    [...qaContentCheckKeys.all, 'results', runId] as const,
};

/**
 * Fetch QA runs for the current tenant with filters.
 */
export function useQARuns(tenantId: string, filters: QAContentCheckFilters) {
  return useQuery<QARun[]>({
    queryKey: qaContentCheckKeys.runs(tenantId, filters),
    queryFn: () => qaContentCheckService.getRuns(tenantId, filters),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });
}

/**
 * Fetch check results for a specific run. Only fetches when runId is provided.
 */
export function useQACheckResults(runId: string | null) {
  return useQuery<QACheckResult[]>({
    queryKey: qaContentCheckKeys.checkResults(runId || ''),
    queryFn: () => qaContentCheckService.getCheckResults(runId!),
    enabled: !!runId,
  });
}
